import { getAuthToken } from '../utils/authUtils'; //
class FacilityManagementService {
  constructor() {
    this.baseURL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
    this.apiPath = '/facility';
  }

  async getAuthHeaders() {
    const token = await getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Get facility details including departments, rooms, and beds
  async getFacility(facilityId) {
    try {
      const headers = await this.getAuthHeaders();
      
      // Get facility details from /facility endpoint
      const facilityResponse = await fetch(`${this.baseURL}${this.apiPath}/facilities/${facilityId}`, {
        method: 'GET',
        headers
      });

      if (!facilityResponse.ok) {
        throw new Error(`Failed to get facility: ${facilityResponse.statusText}`);
      }

      const facilityData = await facilityResponse.json();
      const facility = facilityData.facility || facilityData;

      // Extract departments and rooms from facility data
      const departments = facility.departments || [];
      const rooms = [];
      const beds = [];

      // Process departments and their rooms/beds
      departments.forEach(dept => {
        if (dept.rooms) {
          dept.rooms.forEach(room => {
            rooms.push({
              ...room,
              departmentId: dept.id,
              departmentName: dept.name
            });

            // If room has beds, add them to beds array
            if (room.beds) {
              room.beds.forEach(bed => {
                beds.push({
                  ...bed,
                  departmentId: dept.id,
                  departmentName: dept.name,
                  roomId: room.id,
                  roomName: room.name
                });
              });
            }
          });
        }
      });

      // Get current bed status using bedService
      const { getBedsByFacility } = await import('./bedService');
      const bedStatus = await getBedsByFacility(facilityId);

      // Merge bed status with bed data
      const bedsWithStatus = beds.map(bed => {
        const status = bedStatus.find(s => s.id === bed.id);
        return {
          ...bed,
          status: status?.status || 'unknown',
          occupiedBy: status?.occupiedBy || null
        };
      });

      // Return organized data
      return {
        ...facility,
        departments: departments.map(dept => ({
          ...dept,
          rooms: rooms.filter(room => room.departmentId === dept.id).map(room => ({
            ...room,
            beds: bedsWithStatus.filter(bed => bed.roomId === room.id)
          }))
        }))
      };
    } catch (error) {
      console.error('Error getting facility:', error);
      throw error;
    }
  }

  // Update facility departments
  async updateFacilityDepartments(facilityId, departments) {
    try {
      // First get current facility data
      const currentFacility = await this.getFacility(facilityId);
      
      // Prepare updated facility data
      const updatedFacility = {
        ...currentFacility,
        departments: departments.map(dept => ({
          ...dept,
          // Preserve existing rooms if department already exists
          rooms: (currentFacility.departments?.find(d => d.id === dept.id)?.rooms || [])
        }))
      };

      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseURL}${this.apiPath}/facilities/${facilityId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updatedFacility)
      });

      if (!response.ok) {
        throw new Error(`Failed to update facility departments: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating facility departments:', error);
      throw error;
    }
  }

  // Add department to facility
  async addDepartment(facilityId, department) {
    try {
      // First get current facility data
      const currentFacility = await this.getFacility(facilityId);
      
      // Add new department
      const updatedFacility = {
        ...currentFacility,
        departments: [
          ...(currentFacility.departments || []),
          { ...department, rooms: [] }
        ]
      };

      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseURL}${this.apiPath}/facilities/${facilityId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updatedFacility)
      });

      if (!response.ok) {
        throw new Error(`Failed to add department: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding department:', error);
      throw error;
    }
  }

  // Update department in facility
  async updateDepartment(facilityId, departmentId, departmentData) {
    try {
      // First get current facility data
      const currentFacility = await this.getFacility(facilityId);
      
      // Update specific department
      const updatedFacility = {
        ...currentFacility,
        departments: currentFacility.departments.map(dept => 
          dept.id === departmentId ? {
            ...dept,
            ...departmentData,
            rooms: dept.rooms // Preserve existing rooms
          } : dept
        )
      };

      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseURL}${this.apiPath}/facilities/${facilityId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updatedFacility)
      });

      if (!response.ok) {
        throw new Error(`Failed to update department: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating department:', error);
      throw error;
    }
  }

  // Delete department from facility
  async deleteDepartment(facilityId, departmentId) {
    try {
      // First get current facility data
      const currentFacility = await this.getFacility(facilityId);
      
      // Find the department
      const department = currentFacility.departments.find(d => d.id === departmentId);
      if (!department) {
        throw new Error('Department not found');
      }

      // Check if any rooms in the department have occupied beds
      const { getBedsByFacility } = await import('./bedService');
      const bedStatus = await getBedsByFacility(facilityId);
      
      const occupiedBeds = [];
      department.rooms?.forEach(room => {
        room.beds?.forEach(bed => {
          const status = bedStatus.find(s => s.id === bed.id);
          if (status?.status === 'occupied') {
            occupiedBeds.push({
              bedId: bed.id,
              bedName: bed.name,
              roomName: room.name,
              patientId: status.occupiedBy
            });
          }
        });
      });

      if (occupiedBeds.length > 0) {
        throw new Error(`Cannot delete department. The following beds are occupied: ${
          occupiedBeds.map(b => `${b.bedName} in ${b.roomName}`).join(', ')
        }`);
      }

      // If no occupied beds, proceed with deletion
      const updatedFacility = {
        ...currentFacility,
        departments: currentFacility.departments.filter(dept => dept.id !== departmentId)
      };

      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseURL}${this.apiPath}/facilities/${facilityId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updatedFacility)
      });

      if (!response.ok) {
        throw new Error(`Failed to delete department: ${response.statusText}`);
      }

      // Clean up bed status for all beds in the department
      const { deleteBed } = await import('./bedService');
      await Promise.all(
        department.rooms?.flatMap(room => 
          room.beds?.map(bed => deleteBed(bed.id)) || []
        ) || []
      );

      return await response.json();
    } catch (error) {
      console.error('Error deleting department:', error);
      throw error;
    }
  }

  // Add room to department
  async addRoom(facilityId, departmentId, room) {
    try {
      // First get current facility data
      const currentFacility = await this.getFacility(facilityId);
      
      // Add room to department
      const updatedFacility = {
        ...currentFacility,
        departments: currentFacility.departments.map(dept => 
          dept.id === departmentId ? {
            ...dept,
            rooms: [...(dept.rooms || []), { ...room, beds: [] }]
          } : dept
        )
      };

      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseURL}${this.apiPath}/facilities/${facilityId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updatedFacility)
      });

      if (!response.ok) {
        throw new Error(`Failed to add room: ${response.statusText}`);
      }

      // If room was added successfully and it's not a standalone room,
      // initialize bed status
      if (room.type !== 'standalone' && room.beds?.length > 0) {
        const { createBedStatus } = await import('./bedService');
        await Promise.all(room.beds.map(bed => 
          createBedStatus(bed.id, {
            name: bed.name,
            type: bed.type,
            facilityId,
            departmentId,
            roomId: room.id
          })
        ));
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding room:', error);
      throw error;
    }
  }

  // Update room in department
  async updateRoom(facilityId, departmentId, roomId, roomData) {
    try {
      // First get current facility data
      const currentFacility = await this.getFacility(facilityId);
      
      // Update room in department
      const updatedFacility = {
        ...currentFacility,
        departments: currentFacility.departments.map(dept => 
          dept.id === departmentId ? {
            ...dept,
            rooms: dept.rooms.map(room =>
              room.id === roomId ? {
                ...room,
                ...roomData,
                beds: room.beds // Preserve existing beds
              } : room
            )
          } : dept
        )
      };

      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseURL}${this.apiPath}/facilities/${facilityId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updatedFacility)
      });

      if (!response.ok) {
        throw new Error(`Failed to update room: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating room:', error);
      throw error;
    }
  }

  // Delete room from department
  async deleteRoom(facilityId, departmentId, roomId) {
    try {
      // First get current facility data
      const currentFacility = await this.getFacility(facilityId);
      
      // Get room data before deletion to handle bed cleanup
      const room = currentFacility.departments
        .find(d => d.id === departmentId)
        ?.rooms.find(r => r.id === roomId);

      if (!room) {
        throw new Error('Room not found');
      }

      // Check if any beds in the room are occupied
      if (room.beds?.length > 0) {
        const { getBedsByFacility } = await import('./bedService');
        const bedStatus = await getBedsByFacility(facilityId);
        
        const occupiedBeds = room.beds
          .map(bed => {
            const status = bedStatus.find(s => s.id === bed.id);
            if (status?.status === 'occupied') {
              return {
                bedId: bed.id,
                bedName: bed.name,
                patientId: status.occupiedBy
              };
            }
            return null;
          })
          .filter(Boolean);

        if (occupiedBeds.length > 0) {
          throw new Error(`Cannot delete room. The following beds are occupied: ${
            occupiedBeds.map(b => b.bedName).join(', ')
          }`);
        }
      }

      // If no occupied beds, proceed with deletion
      const updatedFacility = {
        ...currentFacility,
        departments: currentFacility.departments.map(dept => 
          dept.id === departmentId ? {
            ...dept,
            rooms: dept.rooms.filter(r => r.id !== roomId)
          } : dept
        )
      };

      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseURL}${this.apiPath}/facilities/${facilityId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updatedFacility)
      });

      if (!response.ok) {
        throw new Error(`Failed to delete room: ${response.statusText}`);
      }

      // Clean up bed status for all beds in the room
      if (room.beds?.length > 0) {
        const { deleteBed } = await import('./bedService');
        await Promise.all(room.beds.map(bed => deleteBed(bed.id)));
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting room:', error);
      throw error;
    }
  }
}

export default FacilityManagementService;
